import React, { useEffect, useRef, useState } from 'react';

import type { CatalogoOficinaExterna } from '../../../api/oficinaExternaApi';
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../../../shared/ui/modal-close-button.css';

export interface FiltrosDeMinhasVisitas {
  placa?: string;
  de?: string;
  ate?: string;
  regiaoId?: string;
  oficinaId?: string;
  situacao?: 'NA_OFICINA' | 'SAIU';
}

interface Props {
  aplicados: FiltrosDeMinhasVisitas;
  /**
   * O catálogo baixado no aparelho — é dele que saem região e oficina.
   *
   * ⚠️ Sem rede o catálogo ainda existe (é a razão de ele ser baixado), então os seletores
   * continuam funcionando offline. O que não funciona offline é a consulta em si, e disso quem
   * avisa é a lista.
   */
  catalogo: CatalogoOficinaExterna | null;
  onAplicar: (filtros: FiltrosDeMinhasVisitas) => void;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

/**
 * Filtros da aba "Minhas visitas" (25/09/2026).
 *
 * Segue o padrão do sistema: montagem condicional pelo pai, estado local que só sai no "Aplicar",
 * e **recebe o que está aplicado** — sem isso o modal reabre em branco e um "Aplicar" seguinte
 * limparia tudo em silêncio (`BUG-V43`). Como não há resumo dos filtros na tela, reabrir fiel ao
 * que está valendo é a única via de conferir o recorte.
 */
const FiltroMinhasVisitasModal: React.FC<Props> = ({
  aplicados,
  catalogo,
  onAplicar,
  onFechar,
  sidebarRef,
}) => {
  const [placa, setPlaca] = useState(aplicados.placa ?? '');
  const [de, setDe] = useState(aplicados.de ?? '');
  const [ate, setAte] = useState(aplicados.ate ?? '');
  const [regiaoId, setRegiaoId] = useState(aplicados.regiaoId ?? '');
  const [oficinaId, setOficinaId] = useState(aplicados.oficinaId ?? '');
  const [situacao, setSituacao] = useState<string>(aplicados.situacao ?? '');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ref.current?.contains(alvo)) return;
      if (sidebarRef?.current?.contains(alvo)) return;
      onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  // Escolher a região restringe as oficinas: a lista inteira é longa e a árvore já organiza o
  // trabalho por região.
  const oficinas = (catalogo?.oficinas ?? []).filter(
    (o) => !regiaoId || o.regiaoId === regiaoId,
  );

  const limpar = () => {
    setPlaca('');
    setDe('');
    setAte('');
    setRegiaoId('');
    setOficinaId('');
    setSituacao('');
  };

  const aplicar = () => {
    onAplicar({
      placa: placa.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || undefined,
      de: de || undefined,
      ate: ate || undefined,
      regiaoId: regiaoId || undefined,
      // ⚠️ Oficina só vale se ela pertence à região escolhida — trocar a região sem limpar a
      // oficina produziria um recorte impossível, e a lista viria vazia sem explicação.
      oficinaId: oficinas.some((o) => o.id === oficinaId) ? oficinaId : undefined,
      situacao: (situacao as FiltrosDeMinhasVisitas['situacao']) || undefined,
    });
  };

  return (
    <div className="modal-filtro-overlay">
      {/* TRÊS níveis, como o molde do Contac: overlay, `content` (que dá a largura) e o `card`
          branco. Fundir os dois últimos faz o `width: 100%` do card vencer e o modal estica de
          ponta a ponta. */}
      <div className="modal-filtro-content" ref={ref}>
        <div className="filtro-modal-card">
          <div className="filtro-modal-header">
            <h2>Filtros</h2>
            <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
              ×
            </button>
          </div>

          <div className="filtro-modal-body">
            <section className="filtro-section">
              <h3>Período da visita</h3>
              <div className="filtro-grid-2">
                <label>
                  De
                  <input
                    type="date"
                    className="premium-text-input"
                    value={de}
                    onChange={(e) => setDe(e.target.value)}
                  />
                </label>
                <label>
                  Até
                  <input
                    type="date"
                    className="premium-text-input"
                    value={ate}
                    onChange={(e) => setAte(e.target.value)}
                  />
                </label>
              </div>
              {/* ⚠️ É a data em que VOCÊ esteve na oficina, não a do envio: sem rede as duas
                  divergem por dias, e filtrar pela do envio esconderia a visita do dia certo. */}
              <p className="filtro-field-hint">
                A data informada da visita (o dia em que você esteve na oficina), não a do envio.
                Sem período, aparecem todas.
              </p>
            </section>

            <section className="filtro-section">
              <h3>Onde</h3>
              <div className="filtro-grid-2">
                <label>
                  Região
                  <div className="select-wrapper">
                    <select
                      value={regiaoId}
                      onChange={(e) => {
                        setRegiaoId(e.target.value);
                        setOficinaId('');
                      }}
                    >
                      <option value="">Todas</option>
                      {(catalogo?.regioes ?? []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
                <label>
                  Oficina
                  <div className="select-wrapper">
                    <select value={oficinaId} onChange={(e) => setOficinaId(e.target.value)}>
                      <option value="">Todas</option>
                      {oficinas.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
              </div>
              {!catalogo && (
                <p className="filtro-field-hint">
                  Este aparelho ainda não baixou os cadastros, então região e oficina não estão
                  disponíveis — isto não quer dizer que não existam. Os demais filtros funcionam.
                </p>
              )}
            </section>

            <section className="filtro-section">
              <h3>Veículo</h3>
              <div className="filtro-grid-2">
                <label>
                  Placa
                  <input
                    type="text"
                    className="premium-text-input"
                    value={placa}
                    maxLength={10}
                    autoCapitalize="characters"
                    placeholder="ABC1D23"
                    onChange={(e) => setPlaca(e.target.value)}
                  />
                </label>
                <label>
                  Situação hoje
                  <div className="select-wrapper">
                    <select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                      <option value="">Todas</option>
                      <option value="NA_OFICINA">Ainda na oficina</option>
                      <option value="SAIU">Já saiu</option>
                    </select>
                  </div>
                </label>
              </div>
              {/* A situação é do veículo AGORA, não de quando a visita foi feita: uma visita
                  antiga num carro já entregue aparece em "Já saiu". */}
              <p className="filtro-field-hint">
                "Situação hoje" é como o veículo está agora, não como estava no dia da visita.
              </p>
            </section>
          </div>

          <div className="filtro-modal-footer">
            <button
              type="button"
              className="filtro-btn-secondary filtro-btn-limpar"
              onClick={limpar}
            >
              Limpar filtros
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={aplicar}>
              Aplicar filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltroMinhasVisitasModal;
